/*
 * PRD-037 — Layout PROPIO del portal del partner (app separada). Top-nav V4 + navegacion entre
 * paginas de gestion + Outlet. No depende del layout del panel.
 *
 * Responsive: en escritorio el nav es una barra horizontal de pestañas (underline teal en la
 * activa); en celular/tablet (<1024) el nav colapsa tras un boton hamburguesa que despliega un
 * menu en columna con targets tactiles ≥44px, la identidad del partner y "Salir".
 */
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { usePartnerAuth } from './PartnerAuthContext';
import { useDesktop } from '../utils/hooks/useDesktop';
import { C } from './ui';

// Línea divisoria del header (slate sobre slate): teal translúcido sutil, no un borde duro.
const HEADER_HAIRLINE = 'rgba(255,255,255,0.08)';
// Borde del chrome oscuro para botones (hamburguesa / Salir).
const CHROME_BORDER = '#3D5160';
// Fondo del item activo en el menú móvil: teal de marca con baja opacidad sobre el slate.
const MOBILE_ACTIVE_BG = 'rgba(66,152,181,0.22)';

// Pestaña de escritorio: ocupa toda la altura del header y lleva el underline teal en la activa,
// alineado al borde inferior del header (look de tab-bar). El texto activo va en blanco.
const deskTab = (active: boolean) => ({
  color: active ? C.white : '#B7C9D8',
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  height: '100%',
  padding: '0 4px',
  borderBottom: active ? `3px solid ${C.secondary}` : '3px solid transparent',
  marginBottom: -1, // solapa el hairline del header para que el underline quede al ras
  transition: 'color 150ms',
});

// Item del menú móvil: target táctil amplio, fondo teal translúcido + texto blanco en activo.
const mobileItem = (active: boolean) => ({
  color: active ? C.white : '#B7C9D8',
  fontSize: 15,
  fontWeight: active ? 600 : 500,
  textDecoration: 'none',
  display: 'block',
  padding: '12px 14px',
  minHeight: 44,
  borderRadius: 8,
  background: active ? MOBILE_ACTIVE_BG : 'transparent',
  boxSizing: 'border-box' as const,
});

const logoutBtnStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
  color: C.white,
  background: 'transparent',
  border: `1.5px solid ${CHROME_BORDER}`,
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',
};

const PartnerLayout = () => {
  const { partner, logout } = usePartnerAuth();
  const isDesktop = useDesktop();
  const [menuOpen, setMenuOpen] = useState(false);

  const partnerName = partner?.nombre || partner?.usuario || '';

  const links = (
    <>
      <NavLink
        to="/partner"
        end
        style={({ isActive }) => (isDesktop ? deskTab(isActive) : mobileItem(isActive))}
        onClick={() => setMenuOpen(false)}
      >
        Inicio
      </NavLink>
      <NavLink
        to="/partner/comercios"
        style={({ isActive }) => (isDesktop ? deskTab(isActive) : mobileItem(isActive))}
        onClick={() => setMenuOpen(false)}
      >
        Comercios
      </NavLink>
      <NavLink
        to="/partner/comercios/nuevo"
        style={({ isActive }) => (isDesktop ? deskTab(isActive) : mobileItem(isActive))}
        onClick={() => setMenuOpen(false)}
      >
        Nuevo comercio
      </NavLink>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.pageBg, fontFamily: 'Poppins, sans-serif', color: C.primary }}>
      <header style={{ background: C.primary, color: C.white, borderBottom: `1px solid ${HEADER_HAIRLINE}` }}>
        <div
          style={{
            padding: isDesktop ? '0 24px' : '0 14px',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            gap: isDesktop ? 28 : 12,
          }}
        >
          <NavLink
            to="/partner"
            end
            onClick={() => setMenuOpen(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              textDecoration: 'none',
              color: C.white,
              flexShrink: 0,
            }}
            aria-label="Plick Partners — inicio"
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: C.secondary,
                color: C.white,
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1,
              }}
              aria-hidden="true"
            >
              P
            </span>
            <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
              Plick <span style={{ color: C.secondaryLight ?? C.secondary }}>Partners</span>
            </span>
          </NavLink>

          {isDesktop ? (
            <>
              <nav style={{ display: 'flex', gap: 22, alignItems: 'stretch', height: '100%', flex: 1 }}>{links}</nav>
              <span style={{ fontSize: 13, color: '#B7C9D8', whiteSpace: 'nowrap' }}>{partnerName}</span>
              <button type="button" onClick={logout} style={logoutBtnStyle}>
                Salir
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menú"
              aria-expanded={menuOpen}
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                background: menuOpen ? 'rgba(255,255,255,0.08)' : 'transparent',
                border: `1.5px solid ${CHROME_BORDER}`,
                borderRadius: 6,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.white}
                strokeWidth="2"
                strokeLinecap="round"
              >
                {menuOpen ? (
                  <>
                    <line x1="5" y1="5" x2="19" y2="19" />
                    <line x1="19" y1="5" x2="5" y2="19" />
                  </>
                ) : (
                  <>
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>

        {!isDesktop && menuOpen ? (
          <nav
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '6px 12px 14px',
              borderTop: `1px solid ${HEADER_HAIRLINE}`,
            }}
          >
            {links}
            <div style={{ borderTop: `1px solid ${HEADER_HAIRLINE}`, margin: '8px 0 0' }} />
            {partnerName ? (
              <div style={{ padding: '12px 14px 6px' }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#7E94A6',
                  }}
                >
                  Sesión
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.white, marginTop: 2 }}>{partnerName}</div>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
              style={{ ...logoutBtnStyle, width: '100%', padding: '12px 16px', marginTop: 4 }}
            >
              Salir
            </button>
          </nav>
        ) : null}
      </header>

      <main style={{ padding: isDesktop ? '28px 24px' : '18px 16px' }}>
        <div style={{ width: '100%', maxWidth: 880, margin: '0 auto', boxSizing: 'border-box' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PartnerLayout;
